import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Circle as CircleIcon,
} from "@mui/icons-material";

import useApi from "../../hooks/useApi";
import Page from "./Page";
import Loading from "../ui/Loading";

const MessageThread = () => {
  const navigate = useNavigate();
  const { messageId } = useParams();
  const { postData } = useApi();

  const [thread, setThread] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recipientFilter, setRecipientFilter] = useState("");

  useEffect(() => {
    if (messageId) {
      loadThread();
    }
  }, [messageId]);

  const loadThread = async () => {
    setLoading(true);
    const response = await postData({
      entityName: "Message",
      action: "getMessageThread",
      data: { messageId },
    });

    if (response.isSuccessful) {
      setThread(response.data);
    }
    setLoading(false);
  };

  const handleBack = () => {
    navigate("/messages");
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTargetingDisplay = (message) => {
    const targets = [];
    if (message.userId) targets.push("User");
    if (message.clientId) targets.push("Client");
    if (message.projectId) targets.push("Project");
    if (message.province) targets.push(`Province: ${message.province}`);
    if (message.bannerId) targets.push("Banner");
    if (message.retailerId) targets.push("Retailer");
    return targets.length > 0 ? targets.join(", ") : "All";
  };

  const getRecipientStats = (recipients) => {
    const total = recipients?.length || 0;
    const delivered = recipients?.filter((r) => r.deliveredAt).length || 0;
    const read = recipients?.filter((r) => r.readAt).length || 0;
    const acknowledged =
      recipients?.filter((r) => r.acknowledgementStatus === "acknowledged")
        .length || 0;
    return { total, delivered, read, acknowledged };
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "—";
    return new Date(timestamp).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getFilteredRecipients = (recipients) => {
    if (!recipientFilter.trim()) return recipients || [];
    const filter = recipientFilter.toLowerCase();
    return (recipients || []).filter(
      (r) =>
        r.userName?.toLowerCase().includes(filter) ||
        r.userId?.toLowerCase().includes(filter),
    );
  };

  if (loading) {
    return (
      <Page
        pageName="messageThread"
        backPath="/communication"
      >
        <Loading />
      </Page>
    );
  }

  const parentMessage = thread.find((m) => !m.parentMessageId);
  const replies = thread.filter((m) => m.parentMessageId);

  return (
    <Page
      pageName="messageThread"
      backPath="/communication"
    >
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={handleBack}
        >
          Back to Messages
        </Button>
      </Box>

      {!thread || thread.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography color="text.secondary">No message data found</Typography>
        </Paper>
      ) : (
        <>
          {/* Parent Message */}
          {parentMessage && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Typography
                  variant="h5"
                  component="h2"
                >
                  {parentMessage.title}
                </Typography>
                <Chip
                  label={parentMessage.priority}
                  color={
                    parentMessage.priority === "urgent"
                      ? "error"
                      : parentMessage.priority === "high"
                        ? "warning"
                        : "default"
                  }
                />
              </Box>

              <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                {parentMessage.requiresAcknowledgement && (
                  <Chip
                    label="Requires Acknowledgement"
                    size="small"
                    variant="outlined"
                  />
                )}
                {parentMessage.allowReplies && (
                  <Chip
                    label="Replies Allowed"
                    size="small"
                    variant="outlined"
                    color="success"
                  />
                )}
                {parentMessage.category && (
                  <Chip
                    label={parentMessage.category}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>

              <Typography
                variant="body1"
                sx={{ mb: 2, whiteSpace: "pre-wrap" }}
              >
                {parentMessage.text}
              </Typography>

              {parentMessage.text_fr && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, fontWeight: "bold" }}
                  >
                    French Version:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ whiteSpace: "pre-wrap", fontStyle: "italic" }}
                  >
                    {parentMessage.text_fr}
                  </Typography>
                </>
              )}

              {parentMessage.images && parentMessage.images.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, fontWeight: "bold" }}
                  >
                    Images (English):
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    {parentMessage.images.map((img, i) => (
                      <img
                        key={i}
                        src={img}
                        alt={`Attachment ${i + 1}`}
                        style={{
                          maxWidth: "200px",
                          maxHeight: "200px",
                          borderRadius: "4px",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {parentMessage.images_fr &&
                parentMessage.images_fr.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1, fontWeight: "bold" }}
                    >
                      Images (French):
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      {parentMessage.images_fr.map((img, i) => (
                        <img
                          key={i}
                          src={img}
                          alt={`French Attachment ${i + 1}`}
                          style={{
                            maxWidth: "200px",
                            maxHeight: "200px",
                            borderRadius: "4px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Created by {parentMessage.createdByName} on{" "}
                  {formatDate(parentMessage.createdAt)}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Targeting: {getTargetingDisplay(parentMessage)}
                </Typography>
              </Box>

              {/* Recipients Section */}
              {parentMessage.recipients &&
                Array.isArray(parentMessage.recipients) && (
                  <Accordion
                    sx={{
                      mt: 2,
                      boxShadow: "none",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                        >
                          📊 Recipients
                        </Typography>
                        {(() => {
                          const stats = getRecipientStats(
                            parentMessage.recipients,
                          );
                          return (
                            <>
                              <Chip
                                label={`${stats.total} Total`}
                                size="small"
                              />
                              <Chip
                                label={`${stats.delivered} Delivered`}
                                size="small"
                                color="info"
                                variant="outlined"
                              />
                              <Chip
                                label={`${stats.read} Read`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              {parentMessage.requiresAcknowledgement && (
                                <Chip
                                  label={`${stats.acknowledged} Acknowledged`}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                              )}
                            </>
                          );
                        })()}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ mb: 2 }}>
                        <TextField
                          size="small"
                          placeholder="Search recipients..."
                          value={recipientFilter}
                          onChange={(e) => setRecipientFilter(e.target.value)}
                          fullWidth
                        />
                      </Box>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <strong>Name</strong>
                              </TableCell>
                              <TableCell>
                                <strong>Delivered</strong>
                              </TableCell>
                              <TableCell>
                                <strong>Read</strong>
                              </TableCell>
                              {parentMessage.requiresAcknowledgement && (
                                <TableCell>
                                  <strong>Acknowledged</strong>
                                </TableCell>
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {getFilteredRecipients(parentMessage.recipients)
                              .length === 0 ? (
                              <TableRow>
                                <TableCell
                                  colSpan={
                                    parentMessage.requiresAcknowledgement
                                      ? 4
                                      : 3
                                  }
                                  align="center"
                                >
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    No recipients found
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              getFilteredRecipients(parentMessage.recipients)
                                .sort((a, b) => {
                                  // Sort: acknowledged > read > unread
                                  if (
                                    a.acknowledgementStatus ===
                                      "acknowledged" &&
                                    b.acknowledgementStatus !== "acknowledged"
                                  )
                                    return -1;
                                  if (
                                    a.acknowledgementStatus !==
                                      "acknowledged" &&
                                    b.acknowledgementStatus === "acknowledged"
                                  )
                                    return 1;
                                  if (a.readAt && !b.readAt) return -1;
                                  if (!a.readAt && b.readAt) return 1;
                                  return 0;
                                })
                                .map((recipient) => (
                                  <TableRow key={recipient.userId}>
                                    <TableCell>
                                      {recipient.userName || recipient.userId}
                                    </TableCell>
                                    <TableCell>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                        }}
                                      >
                                        {recipient.deliveredAt ? (
                                          <>
                                            <CheckIcon
                                              fontSize="small"
                                              color="success"
                                            />
                                            <Typography variant="caption">
                                              {formatTimestamp(
                                                recipient.deliveredAt,
                                              )}
                                            </Typography>
                                          </>
                                        ) : (
                                          <>
                                            <CircleIcon
                                              fontSize="small"
                                              sx={{ color: "text.disabled" }}
                                            />
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              —
                                            </Typography>
                                          </>
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                        }}
                                      >
                                        {recipient.readAt ? (
                                          <>
                                            <CheckIcon
                                              fontSize="small"
                                              color="primary"
                                            />
                                            <Typography variant="caption">
                                              {formatTimestamp(
                                                recipient.readAt,
                                              )}
                                            </Typography>
                                          </>
                                        ) : (
                                          <>
                                            <CircleIcon
                                              fontSize="small"
                                              sx={{ color: "text.disabled" }}
                                            />
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              —
                                            </Typography>
                                          </>
                                        )}
                                      </Box>
                                    </TableCell>
                                    {parentMessage.requiresAcknowledgement && (
                                      <TableCell>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          {recipient.acknowledgementStatus ===
                                          "acknowledged" ? (
                                            <>
                                              <CheckIcon
                                                fontSize="small"
                                                color="success"
                                              />
                                              <Typography variant="caption">
                                                {formatTimestamp(
                                                  recipient.acknowledgedAt,
                                                )}
                                              </Typography>
                                            </>
                                          ) : (
                                            <>
                                              <CircleIcon
                                                fontSize="small"
                                                sx={{ color: "text.disabled" }}
                                              />
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                              >
                                                —
                                              </Typography>
                                            </>
                                          )}
                                        </Box>
                                      </TableCell>
                                    )}
                                  </TableRow>
                                ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                )}
            </Paper>
          )}

          {/* Replies */}
          {replies.length > 0 && (
            <Box>
              <Typography
                variant="h6"
                sx={{ mb: 2 }}
              >
                Replies ({replies.length})
              </Typography>

              {replies.map((reply, index) => (
                <Paper
                  key={reply._id}
                  sx={{
                    p: 2,
                    mb: 2,
                    ml: 4,
                    borderLeft: "4px solid",
                    borderColor: "primary.main",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      color="primary"
                      fontWeight="bold"
                    >
                      {reply.createdByName}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      {formatDate(reply.createdAt)}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{ mb: 1, whiteSpace: "pre-wrap" }}
                  >
                    {reply.text}
                  </Typography>

                  {reply.text_fr && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: "pre-wrap", fontStyle: "italic" }}
                    >
                      {reply.text_fr}
                    </Typography>
                  )}

                  {reply.images && reply.images.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Images:
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          flexWrap: "wrap",
                          mt: 0.5,
                        }}
                      >
                        {reply.images.map((img, i) => (
                          <img
                            key={i}
                            src={img}
                            alt={`Reply attachment ${i + 1}`}
                            style={{
                              maxWidth: "150px",
                              maxHeight: "150px",
                              borderRadius: "4px",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {reply.images_fr && reply.images_fr.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Images (French):
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          flexWrap: "wrap",
                          mt: 0.5,
                        }}
                      >
                        {reply.images_fr.map((img, i) => (
                          <img
                            key={i}
                            src={img}
                            alt={`French reply attachment ${i + 1}`}
                            style={{
                              maxWidth: "150px",
                              maxHeight: "150px",
                              borderRadius: "4px",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Paper>
              ))}
            </Box>
          )}

          {replies.length === 0 && (
            <Paper sx={{ p: 3, textAlign: "center" }}>
              <Typography
                variant="body1"
                color="text.secondary"
              >
                No replies yet
              </Typography>
            </Paper>
          )}
        </>
      )}
    </Page>
  );
};

export default MessageThread;
